/**
 * Authoritative residential fee day calculator (RAC_DAY_CALC_V3).
 *
 * Residential billing passes a rate row into `makeRateResolver`. The legacy
 * dollar table and `residentialFeeRatesFor` stay on `award-data/residential.ts`
 * and still fall back to the first row before 1 November 2025.
 * Tests import the published cents fixture from `__fixtures__/rate-periods.v3`.
 * This barrel does not re-export it.
 */

export {
  RESIDENTIAL_ARRANGEMENT_FAMILIES,
  RESIDENTIAL_FEE_COHORTS,
  cohortForArrangementFamily,
} from './cohorts';
export type { ResidentialArrangementFamily, ResidentialFeeCohort } from './cohorts';

export {
  HOSPITAL_LEAVE_FULL_SUBSIDY_DAYS,
  RESIDENTIAL_DAY_STATES,
  SOCIAL_LEAVE_DAYS_PER_FINANCIAL_YEAR,
  classifyResidentialDays,
} from './day-states';
export type {
  ClassifiedResidentialDay,
  ClassifyResidentialDaysInput,
  ResidentialAbsenceSpan,
  ResidentialDayState,
} from './day-states';

export {
  MTCF_ANNUAL_CAP_LAST_DAY,
  MTCF_ANNUAL_CAP_WINDOW_END,
  MTCF_ANNUAL_CAP_WINDOW_START,
  RESIDENTIAL_ACCOMMODATION_MEANS_CLASSES,
  addCalendarMonths,
  computeResidentialFeeDay,
  emptyResidentialFeePositions,
  mtcfLateInformationApplies,
  normalizeResidentialFeeMeans,
  resolveMtcfAnnualCapPeriod,
} from './day-calculator';
export type { MtcfAnnualCapPeriod } from './day-calculator';
export type {
  ResidentialAccommodationContributionInput,
  ResidentialAccommodationMeansClass,
  ResidentialBedReservationInput,
  ResidentialFeeCapOverrides,
  ResidentialFeeComponentCents,
  ResidentialFeeDayInput,
  ResidentialFeeDayResult,
  ResidentialFeeMeans,
  ResidentialFeeMeansInput,
  ResidentialFeePositions,
} from './day-calculator';

export {
  RESIDENTIAL_COMPONENT_GST,
  RESIDENTIAL_FEE_COMPONENTS,
  RESIDENTIAL_GST_TREATMENTS,
  gstTreatmentForResidentialComponent,
} from './gst';
export type { ResidentialFeeComponent, ResidentialGstTreatment } from './gst';

export { computeResidentialFeePeriod } from './period';
export type {
  ResidentialFeePeriodDay,
  ResidentialFeePeriodInput,
  ResidentialFeePeriodResult,
  ResidentialFeePeriodTotals,
} from './period';

export {
  DEFAULT_RESIDENTIAL_FEE_POLICY_V3,
  RAC_DAY_CALC_VERSION,
  resolveResidentialFeePolicy,
} from './policy';
export type {
  ResidentialDepartureMeansPolicy,
  ResidentialFeePolicyV3,
  ResidentialMeansPendingPolicy,
} from './policy';

export {
  MTCF_UNDER_ONE_DOLLAR_CENTS,
  NCCC_TIME_LIMIT_DAYS,
  assertResidentialFeeDate,
  assertResidentialFeeRatePeriod,
  makeRateResolver,
} from './rates';
export type { ResidentialFeeRatePeriodV3, ResidentialFeeRateResolver } from './rates';

export { RESIDENTIAL_FEE_REASON_CODES, orderResidentialFeeReasonCodes } from './reason-codes';
export type { ResidentialFeeReasonCode } from './reason-codes';

export {
  earliestCapTrigger,
  parseResidentialCapOpening,
  rebaseResidentialCapOpening,
} from './cap-opening';
export type {
  ResidentialCapInRunDay,
  ResidentialCapOpening,
  ResidentialCapRebase,
} from './cap-opening';

export { residentialStayExit, residentialStayMeansFacts } from './stay-facts';
export type {
  ResidentialStayExit,
  ResidentialStayExitRow,
  ResidentialStayExitType,
  ResidentialStayHoldCode,
  ResidentialStayMeansFacts,
  ResidentialStayMeansResult,
} from './stay-facts';

export {
  BED_RESERVATION_DAY_STATUSES,
  HELF_AGREEMENT_KINDS,
  HELF_CHARGE_FREQUENCIES,
  HELF_CPI_BASE_FLOOR_YEAR,
  HELF_DAY_LINE_STATUSES,
  HELF_FIRST_INDEXATION_YEAR,
  HELF_PRE_TRANSITION_SERVICE_BASES,
  HELF_SUSPENSION_KINDS,
  helfDailyCents,
  helfIndexationFactorThousandths,
  helfIndexedAmountCents,
  parseHelfCpiIndex,
  residentialAgreementDayTags,
  resolveBedReservationDay,
  resolveHelfDay,
} from './helf';
export type {
  BedReservationAgreementDayInput,
  BedReservationDayResult,
  BedReservationDayStatus,
  HelfAgreementDayInput,
  HelfAgreementKind,
  HelfChargeFrequency,
  HelfCpiMarchIndex,
  HelfDayLine,
  HelfDayLineStatus,
  HelfDayResult,
  HelfIndexedAmount,
  HelfRegime,
  HelfPreTransitionServiceBasis,
  HelfTransitionFactsInput,
  HelfOldFeeChargeInput,
  HelfSuspensionDayInput,
  HelfSuspensionKind,
  ResidentialAgreementDayTags,
} from './helf';
